// SVG-Zeichenebene über der MapLibre-Karte.
//
// Quelle der Wahrheit pro Objekt = polygon (LatLng[]). Pro Frame projizieren wir
// mit map.project() nach Bildschirm-Pixeln und zeichnen das SVG in genau diesem
// Pixelraum — dadurch sind Strichstärken/Schriftgrößen/Handles konstant (kein
// /scale). Eingaben kommen via map.unproject() zurück nach LatLng.
import { useEffect, useRef, useState, useCallback } from "react";
import type maplibregl from "maplibre-gl";
import type { LatLng, Pt } from "./geo.js";
import {
  pointInPoly,
  distPx,
  distToSegmentPx,
  edgeLengthM,
  midPt,
  areaM2,
  centroidLL,
} from "./geo.js";
import type { EditorObject, Tool, SemanticSelection } from "./types.js";
import { edgeTypeLabel } from "./semantic-edit.js";

const EDGE_COLORS: Record<string, string> = {
  first: "#dc2626",
  traufe: "#2563eb",
  ortgang: "#7c3aed",
  grat: "#ea580c",
  kehle: "#0891b2",
  dachbruch: "#65a30d",
};
const EDGE_HIT = 8; // px Trefferradius Kante

const HANDLE_HIT = 10; // px Trefferradius Eck-Handle
const HANDLE_R = 5; // px Radius Eck-Handle

interface OverlayProps {
  map: maplibregl.Map | null;
  tool: Tool;
  paused: boolean; // 3D-Modus: Bearbeitung pausiert
  objects: EditorObject[];
  buildingPts: LatLng[];
  showBuilding: boolean;
  showRoofs: boolean;
  showObstacles: boolean;
  selectedId: string | null;
  drawTargetKind: "roof" | "obstacle";
  onSelect: (id: string | null) => void;
  /** lokale Geometrie-Änderung eines Objekts (während Drag) */
  onObjectGeom: (id: string, polygon: LatLng[]) => void;
  /** Geste beendet → persistieren */
  onCommit: () => void;
  /** neues Polygon fertig gezeichnet */
  onCreatePolygon: (polygon: LatLng[]) => void;
  /** Stage 2b: neue Dachkante (2-Punkt-Linie) fertig gezeichnet */
  onCreateEdge: (points: LatLng[]) => void;
  /** Stage 2b: neue Sperrfläche (Polygon) fertig gezeichnet */
  onCreateKeepout: (points: LatLng[]) => void;
  /** Stage 2b: aktuelle Auswahl eines semantischen Objekts (Kante/Sperrfläche) */
  selectedSemantic: SemanticSelection | null;
  onSelectSemantic: (sel: SemanticSelection | null) => void;
  onHint: (text: string) => void;
  onDeleteSelected: () => void;
  /** Drohnenbild-Ausrichtmodus: Ziehen verschiebt das Bild statt der Karte. */
  droneAlign?: boolean;
  onDroneMove?: (dLat: number, dLng: number) => void;
  /** Modulbelegung als Overlay (Ecken in LatLng, parallel zur Traufe). Mit dem
   *  „module"-Werkzeug einzeln an/aus schaltbar; deaktivierte werden ausgegraut. */
  moduleRects?: { id: string; roofId: string; cellId: string; corners: LatLng[]; disabled: boolean }[];
  /** Klick auf ein Modul im „module"-Werkzeug → an/aus schalten (persistiert). */
  onToggleModule?: (roofId: string, cellId: string) => void;
}

type DragState =
  | { type: "vertex"; id: string; vi: number }
  | { type: "poly"; id: string; prev: LatLng }
  | { type: "pan"; x: number; y: number }
  | { type: "measure"; idx: number }
  | null;

export function RoofCanvasOverlay(props: OverlayProps) {
  const { map } = props;
  const svgRef = useRef<SVGSVGElement>(null);
  const [, setTick] = useState(0);
  const forceRender = useCallback(() => setTick((t) => (t + 1) % 1_000_000), []);

  const dragRef = useRef<DragState>(null);
  const [draftPoly, setDraftPoly] = useState<LatLng[]>([]);
  const [draftRect, setDraftRect] = useState<{ a: Pt; b: Pt } | null>(null);
  const draftRectStartRef = useRef<Pt | null>(null);
  const [measure, setMeasure] = useState<LatLng[]>([]);
  // Stage 2b: Kante zeichnen (2-Klick) + Hover-Vorschau
  const [draftEdge, setDraftEdge] = useState<LatLng[]>([]);
  const [hoverLL, setHoverLL] = useState<LatLng | null>(null);

  // Werkzeugwechsel: laufende Zeichen-/Hover-Drafts verwerfen (kein Geister-Gummiband).
  useEffect(() => {
    setDraftEdge([]);
    setHoverLL(null);
    setDraftPoly([]);
    setDraftRect(null);
    draftRectStartRef.current = null;
  }, [props.tool]);

  // Re-Render bei Karten-Pan/Zoom/Pitch
  useEffect(() => {
    if (!map) return;
    map.on("move", forceRender);
    map.on("render", forceRender);
    return () => {
      map.off("move", forceRender);
      map.off("render", forceRender);
    };
  }, [map, forceRender]);

  const toPx = useCallback(
    (ll: LatLng): Pt => {
      if (!map) return { x: 0, y: 0 };
      const p = map.project([ll.lng, ll.lat]);
      return { x: p.x, y: p.y };
    },
    [map],
  );

  const cursorLL = useCallback(
    (e: { clientX: number; clientY: number }): LatLng => {
      const rect = svgRef.current?.getBoundingClientRect();
      const x = e.clientX - (rect?.left ?? 0);
      const y = e.clientY - (rect?.top ?? 0);
      const ll = map?.unproject([x, y]);
      return { lat: ll?.lat ?? 0, lng: ll?.lng ?? 0 };
    },
    [map],
  );
  const cursorPx = useCallback((e: { clientX: number; clientY: number }): Pt => {
    const rect = svgRef.current?.getBoundingClientRect();
    return { x: e.clientX - (rect?.left ?? 0), y: e.clientY - (rect?.top ?? 0) };
  }, []);

  // ── Hint je nach Werkzeug ──
  useEffect(() => {
    const hints: Record<Tool, string> = {
      select: "Fläche anklicken zum Auswählen · Eckpunkte ziehen zum Verformen",
      pan: "Ziehen verschiebt die Karte · Mausrad zoomt",
      rect: "Rechteck aufziehen erzeugt eine Fläche",
      polygon: "Eckpunkte klicken · Doppelklick schließt das Polygon",
      measure: "Strecke aufziehen · Endpunkte ziehen zum Anpassen",
      angle: "Drei Punkte klicken → Winkel",
      edge: "Dachkante: Startpunkt klicken → Endpunkt klicken (zuerst Dachfläche wählen)",
      keepout: "Sperrfläche: Eckpunkte klicken · Doppelklick schließt (zuerst Dachfläche wählen)",
      module: "Einzelnes Modul anklicken → an/aus (z. B. über Dachfenster/Kamin entfernen)",
    };
    props.onHint(props.paused ? "3D-Ansicht — Bearbeitung pausiert" : hints[props.tool]);
  }, [props.tool, props.paused, props.onHint]);

  // ── Tastatur: ESC bricht Draft ab, Entf löscht Auswahl ──
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setDraftPoly([]);
        setDraftRect(null);
        setMeasure([]);
        setDraftEdge([]);
        draftRectStartRef.current = null;
      } else if (e.key === "Delete" || e.key === "Backspace") {
        const tag = (e.target as HTMLElement)?.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA") return;
        if (props.selectedId) props.onDeleteSelected();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [props.selectedId, props.onDeleteSelected]);

  // ── Mausrad: Zoom zum Cursor (MapLibre easeTo around) ──
  useEffect(() => {
    const el = svgRef.current;
    if (!el || !map) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const around = map.unproject([e.clientX - rect.left, e.clientY - rect.top]);
      const delta = e.deltaY < 0 ? 0.4 : -0.4;
      const z = Math.min(22, Math.max(1, map.getZoom() + delta));
      map.easeTo({ zoom: z, around, duration: 120 });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [map, props.tool]);

  const visible = (o: EditorObject): boolean => {
    if (o.hidden) return false;
    return o.kind === "roof" ? props.showRoofs : props.showObstacles;
  };

  // ── Pointer-Handler ──────────────────────────────────────────────────────
  const startPan = (e: React.PointerEvent) => {
    const p = cursorPx(e);
    dragRef.current = { type: "pan", x: p.x, y: p.y };
    (e.target as Element).setPointerCapture?.(e.pointerId);
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (!map || props.paused) return;
    const tool = props.tool;
    if (tool === "pan") {
      startPan(e); // Karte verschieben (Overlay-eigen, da SVG die Events fängt)
      return;
    }

    if (tool === "module") {
      // Einzelnes Modul an/aus schalten: oberstes getroffenes Modul (reverse).
      const cur = cursorPx(e);
      const mods = props.moduleRects ?? [];
      for (let i = mods.length - 1; i >= 0; i--) {
        const m = mods[i]!;
        if (pointInPoly(m.corners.map(toPx), cur)) {
          props.onToggleModule?.(m.roofId, m.cellId);
          return;
        }
      }
      return; // Leerraum im Modul-Modus → nichts tun (kein Pan/Zeichnen)
    }

    if (tool === "select") {
      const cur = cursorPx(e);
      // 1) Eck-Handle des ausgewählten Objekts?
      const sel = props.objects.find((o) => o.id === props.selectedId);
      if (sel && !sel.locked && visible(sel)) {
        for (let i = 0; i < sel.polygon.length; i++) {
          const vp = toPx(sel.polygon[i]!);
          if (distPx(vp, cur) < HANDLE_HIT) {
            dragRef.current = { type: "vertex", id: sel.id, vi: i };
            (e.target as Element).setPointerCapture?.(e.pointerId);
            return;
          }
        }
      }
      // 1b) Treffer auf eine semantische Kante/Sperrfläche (liegen oben)?
      for (let k = props.objects.length - 1; k >= 0; k--) {
        const o = props.objects[k]!;
        if (o.kind !== "roof" || !visible(o)) continue;
        for (const ed of o.semantic.edges) {
          if (ed.points.length < 2) continue;
          const a = toPx({ lat: ed.points[0]!.lat, lng: ed.points[0]!.lng });
          const b = toPx({ lat: ed.points[1]!.lat, lng: ed.points[1]!.lng });
          if (distToSegmentPx(cur, a, b) < EDGE_HIT) {
            props.onSelectSemantic({ surfaceId: o.id, kind: "edge", id: ed.id });
            return;
          }
        }
        for (const ko of o.semantic.keepouts) {
          if (ko.points.length < 3) continue;
          if (pointInPoly(ko.points.map((p) => toPx({ lat: p.lat, lng: p.lng })), cur)) {
            props.onSelectSemantic({ surfaceId: o.id, kind: "keepout", id: ko.id });
            return;
          }
        }
      }
      // 2) Treffer auf ein Objekt (oberstes zuerst → reverse)
      for (let k = props.objects.length - 1; k >= 0; k--) {
        const o = props.objects[k]!;
        if (o.locked || !visible(o) || o.polygon.length < 3) continue;
        const screenPts = o.polygon.map(toPx);
        if (pointInPoly(screenPts, cur)) {
          if (o.id !== props.selectedId) props.onSelect(o.id);
          dragRef.current = { type: "poly", id: o.id, prev: cursorLL(e) };
          (e.target as Element).setPointerCapture?.(e.pointerId);
          return;
        }
      }
      // 3) Leerraum → Auswahl aufheben + Karte verschieben (wie pvXpert)
      props.onSelect(null);
      startPan(e);
      return;
    }

    if (tool === "rect") {
      const p = cursorPx(e);
      draftRectStartRef.current = p;
      setDraftRect({ a: p, b: p });
      (e.target as Element).setPointerCapture?.(e.pointerId);
      return;
    }

    if (tool === "polygon") {
      setDraftPoly((d) => [...d, cursorLL(e)]);
      return;
    }

    if (tool === "measure" || tool === "angle") {
      const cur = cursorPx(e);
      // 1) vorhandenen Mess-Endpunkt greifen → ziehen
      for (let i = 0; i < measure.length; i++) {
        if (distPx(toPx(measure[i]!), cur) < HANDLE_HIT) {
          dragRef.current = { type: "measure", idx: i };
          (e.target as Element).setPointerCapture?.(e.pointerId);
          return;
        }
      }
      if (tool === "measure") {
        // 2) Gummiband: Strecke aufziehen (Start = hier, Ende folgt dem Cursor)
        const ll = cursorLL(e);
        setMeasure([ll, ll]);
        dragRef.current = { type: "measure", idx: 1 };
        (e.target as Element).setPointerCapture?.(e.pointerId);
        return;
      }
      // Winkel: weiterhin 3 Punkte per Klick (danach Endpunkte ziehbar)
      setMeasure((m) => [...m, cursorLL(e)].slice(-3));
      return;
    }

    if (tool === "edge") {
      // 2-Klick-Linie: 1. Klick = Start, 2. Klick = Ende → onCreateEdge
      const ll = cursorLL(e);
      if (draftEdge.length === 0) {
        setDraftEdge([ll]);
      } else {
        props.onCreateEdge([draftEdge[0]!, ll]);
        setDraftEdge([]);
      }
      return;
    }

    if (tool === "keepout") {
      // Polygon per Klick; Doppelklick schließt (siehe onDoubleClick)
      setDraftPoly((d) => [...d, cursorLL(e)]);
      return;
    }
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!map || props.paused) return;
    const drag = dragRef.current;
    if (drag) {
      if (drag.type === "pan") {
        const cur = cursorPx(e);
        if (props.droneAlign && props.onDroneMove) {
          // Ausrichtmodus: Drohnenbild dem Cursor folgen lassen (Karte bleibt fix).
          const a = map.unproject([drag.x, drag.y]);
          const b = map.unproject([cur.x, cur.y]);
          props.onDroneMove(b.lat - a.lat, b.lng - a.lng);
        } else {
          // Greifen: Karteninhalt folgt dem Cursor → Kamera gegenläufig verschieben.
          map.panBy([-(cur.x - drag.x), -(cur.y - drag.y)], { animate: false });
        }
        dragRef.current = { type: "pan", x: cur.x, y: cur.y };
        return;
      }
      if (drag.type === "measure") {
        const ll = cursorLL(e);
        setMeasure((m) => m.map((p, i) => (i === drag.idx ? ll : p)));
        return;
      }
      if (drag.type === "vertex") {
        const o = props.objects.find((x) => x.id === drag.id);
        if (!o) return;
        const next = o.polygon.map((p, i) => (i === drag.vi ? cursorLL(e) : p));
        props.onObjectGeom(drag.id, next);
      } else if (drag.type === "poly") {
        const o = props.objects.find((x) => x.id === drag.id);
        if (!o) return;
        const cur = cursorLL(e);
        const dLat = cur.lat - drag.prev.lat;
        const dLng = cur.lng - drag.prev.lng;
        const next = o.polygon.map((p) => ({ lat: p.lat + dLat, lng: p.lng + dLng }));
        props.onObjectGeom(drag.id, next);
        dragRef.current = { ...drag, prev: cur };
      }
      return;
    }
    if (props.tool === "rect" && draftRectStartRef.current) {
      setDraftRect({ a: draftRectStartRef.current, b: cursorPx(e) });
    }
    if (props.tool === "edge" && draftEdge.length === 1) {
      setHoverLL(cursorLL(e));
    }
  };

  const onPointerUp = () => {
    if (dragRef.current) {
      const wasGeom = dragRef.current.type === "vertex" || dragRef.current.type === "poly";
      dragRef.current = null;
      if (wasGeom) props.onCommit(); // nur Objekt-Geometrie persistieren, nicht Pan
      return;
    }
    if (props.tool === "rect" && draftRect && draftRectStartRef.current) {
      const a = draftRect.a;
      const b = draftRect.b;
      draftRectStartRef.current = null;
      setDraftRect(null);
      if (Math.abs(b.x - a.x) > 3 && Math.abs(b.y - a.y) > 3) {
        const corners: Pt[] = [
          { x: a.x, y: a.y },
          { x: b.x, y: a.y },
          { x: b.x, y: b.y },
          { x: a.x, y: b.y },
        ];
        const poly = corners.map((c) => cursorLL({ clientX: c.x + (svgRef.current?.getBoundingClientRect().left ?? 0), clientY: c.y + (svgRef.current?.getBoundingClientRect().top ?? 0) }));
        props.onCreatePolygon(poly);
      }
    }
  };

  const onDoubleClick = () => {
    if (props.tool === "polygon" && draftPoly.length >= 3) {
      props.onCreatePolygon(draftPoly);
      setDraftPoly([]);
    } else if (props.tool === "keepout" && draftPoly.length >= 3) {
      props.onCreateKeepout(draftPoly);
      setDraftPoly([]);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  const w = map?.getContainer().clientWidth ?? 0;
  const h = map?.getContainer().clientHeight ?? 0;
  const ptsToStr = (pts: Pt[]) => pts.map((p) => `${p.x},${p.y}`).join(" ");

  // Overlay fängt alle Pointer (auch Pan) selbst — nur im 3D-Modus deaktiviert.
  const interactive = !props.paused;

  return (
    <svg
      ref={svgRef}
      width={w}
      height={h}
      style={{
        position: "absolute",
        left: 0,
        top: 0,
        width: "100%",
        height: "100%",
        pointerEvents: interactive ? "auto" : "none",
        cursor:
          props.tool === "select"
            ? "default"
            : props.tool === "pan"
            ? "grab"
            : "crosshair",
        overflow: "visible",
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onDoubleClick={onDoubleClick}
    >
      {/* Gebäude-Grundriss (rot gestrichelt) */}
      {props.showBuilding && props.buildingPts.length >= 3 && (
        <polygon
          points={ptsToStr(props.buildingPts.map(toPx))}
          fill="#ef4444"
          fillOpacity={0.05}
          stroke="#ef4444"
          strokeWidth={2}
          strokeDasharray="6 4"
        />
      )}

      {/* Objekte */}
      {props.objects.map((o) => {
        if (!visible(o) || o.polygon.length < 3) return null;
        const sp = o.polygon.map(toPx);
        const sel = o.id === props.selectedId;
        const c = toPx(centroidLL(o.polygon));
        const a = areaM2(o.polygon);
        return (
          <g key={o.id}>
            <polygon
              points={ptsToStr(sp)}
              fill={o.color}
              fillOpacity={o.kind === "obstacle" ? 0.42 : 0.3}
              stroke={o.color}
              strokeWidth={sel ? 3.5 : 2}
              strokeLinejoin="round"
            />
            {/* Name + Fläche am Zentroid */}
            <text
              x={c.x}
              y={c.y}
              fontSize={12}
              fill="#0f172a"
              stroke="#fff"
              strokeWidth={3}
              paintOrder="stroke"
              textAnchor="middle"
              fontWeight={700}
              style={{ pointerEvents: "none" }}
            >
              {o.name}
            </text>
            <text
              x={c.x}
              y={c.y + 15}
              fontSize={10.5}
              fill="#0f172a"
              stroke="#fff"
              strokeWidth={2.6}
              paintOrder="stroke"
              textAnchor="middle"
              fontWeight={600}
              style={{ pointerEvents: "none" }}
            >
              {a.toFixed(1)} m²
            </text>
            {/* Auswahl: Kanten-Maße + Eck-Handles */}
            {sel &&
              o.polygon.map((p, i) => {
                const next = o.polygon[(i + 1) % o.polygon.length]!;
                const mid = midPt(sp[i]!, toPx(next));
                const len = edgeLengthM(p, next);
                return (
                  <text
                    key={`e${i}`}
                    x={mid.x}
                    y={mid.y}
                    fontSize={10}
                    fill="#b45309"
                    stroke="#fff"
                    strokeWidth={2.4}
                    paintOrder="stroke"
                    textAnchor="middle"
                    fontWeight={700}
                    style={{ pointerEvents: "none" }}
                  >
                    {len.toFixed(2)} m
                  </text>
                );
              })}
            {sel &&
              sp.map((p, i) => (
                <circle
                  key={`h${i}`}
                  cx={p.x}
                  cy={p.y}
                  r={HANDLE_R}
                  fill="#fff"
                  stroke={o.color}
                  strokeWidth={2.5}
                />
              ))}
          </g>
        );
      })}

      {/* Semantische Sperrflächen (unter den Kanten) */}
      {props.objects.map((o) =>
        o.kind !== "roof" || !visible(o)
          ? null
          : o.semantic.keepouts.map((ko) => {
              if (ko.points.length < 3) return null;
              const sp = ko.points.map((p) => toPx({ lat: p.lat, lng: p.lng }));
              const sel =
                props.selectedSemantic?.kind === "keepout" && props.selectedSemantic.id === ko.id;
              return (
                <polygon
                  key={`ko-${ko.id}`}
                  points={ptsToStr(sp)}
                  fill="#f97316"
                  fillOpacity={sel ? 0.4 : 0.22}
                  stroke="#ea580c"
                  strokeWidth={sel ? 3 : 1.6}
                  strokeDasharray="4 3"
                />
              );
            }),
      )}

      {/* Semantische Dachkanten (First/Traufe/…) */}
      {props.objects.map((o) =>
        o.kind !== "roof" || !visible(o)
          ? null
          : o.semantic.edges.map((ed) => {
              if (ed.points.length < 2) return null;
              const a = toPx({ lat: ed.points[0]!.lat, lng: ed.points[0]!.lng });
              const b = toPx({ lat: ed.points[1]!.lat, lng: ed.points[1]!.lng });
              const sel = props.selectedSemantic?.kind === "edge" && props.selectedSemantic.id === ed.id;
              const col = EDGE_COLORS[ed.type] ?? "#334155";
              const mid = midPt(a, b);
              return (
                <g key={`ed-${ed.id}`}>
                  <line
                    x1={a.x}
                    y1={a.y}
                    x2={b.x}
                    y2={b.y}
                    stroke={col}
                    strokeWidth={sel ? 5 : 3}
                    strokeLinecap="round"
                  />
                  <text
                    x={mid.x}
                    y={mid.y - 6}
                    fontSize={10}
                    fill={col}
                    stroke="#fff"
                    strokeWidth={2.6}
                    paintOrder="stroke"
                    textAnchor="middle"
                    fontWeight={700}
                    style={{ pointerEvents: "none" }}
                  >
                    {edgeTypeLabel(ed.type)}
                  </text>
                </g>
              );
            }),
      )}

      {/* Modulbelegung (blaue Rechtecke, parallel zur Traufe). Deaktivierte Module
          (manuell entfernt) grau + gestrichelt. Mit dem „module"-Werkzeug anklickbar
          (Hit-Test läuft über den SVG-Root, daher pointerEvents:none je Modul). */}
      {(props.moduleRects ?? []).map((m) => (
        <polygon
          key={`mod-${m.id}`}
          points={ptsToStr(m.corners.map(toPx))}
          fill={m.disabled ? "#94a3b8" : "#2563eb"}
          fillOpacity={m.disabled ? 0.22 : 0.5}
          stroke={m.disabled ? "#64748b" : "#1e3a8a"}
          strokeWidth={0.6}
          strokeDasharray={m.disabled ? "2 2" : undefined}
          style={{ pointerEvents: "none" }}
        />
      ))}

      {/* Draft-Kante (Gummiband bis Cursor) */}
      {draftEdge.length === 1 && hoverLL && (
        <line
          x1={toPx(draftEdge[0]!).x}
          y1={toPx(draftEdge[0]!).y}
          x2={toPx(hoverLL).x}
          y2={toPx(hoverLL).y}
          stroke="#dc2626"
          strokeWidth={2.5}
          strokeDasharray="6 3"
        />
      )}

      {/* Draft-Polygon */}
      {draftPoly.length > 0 && (
        <>
          <polyline
            points={ptsToStr(draftPoly.map(toPx))}
            fill="none"
            stroke="#2563eb"
            strokeWidth={2}
            strokeDasharray="5 3"
          />
          {draftPoly.map((p, i) => {
            const px = toPx(p);
            return <circle key={`dv${i}`} cx={px.x} cy={px.y} r={4.5} fill="#2563eb" stroke="#fff" strokeWidth={2} />;
          })}
        </>
      )}

      {/* Draft-Rechteck */}
      {draftRect && (
        <rect
          x={Math.min(draftRect.a.x, draftRect.b.x)}
          y={Math.min(draftRect.a.y, draftRect.b.y)}
          width={Math.abs(draftRect.b.x - draftRect.a.x)}
          height={Math.abs(draftRect.b.y - draftRect.a.y)}
          fill="#2563eb"
          fillOpacity={0.18}
          stroke="#2563eb"
          strokeWidth={2}
        />
      )}

      {/* Messen / Winkel */}
      {measure.length > 0 && (
        <>
          <polyline points={ptsToStr(measure.map(toPx))} fill="none" stroke="#f59e0b" strokeWidth={2.4} />
          {measure.map((p, i) => {
            const px = toPx(p);
            return <circle key={`mv${i}`} cx={px.x} cy={px.y} r={4} fill="#f59e0b" stroke="#fff" strokeWidth={2} />;
          })}
          {measure.length >= 2 &&
            (() => {
              const mid = midPt(toPx(measure[0]!), toPx(measure[1]!));
              const len = edgeLengthM(measure[0]!, measure[1]!);
              return (
                <text
                  x={mid.x}
                  y={mid.y - 6}
                  fontSize={12}
                  fill="#b45309"
                  stroke="#fff"
                  strokeWidth={3}
                  paintOrder="stroke"
                  textAnchor="middle"
                  fontWeight={700}
                >
                  {len.toFixed(2)} m
                </text>
              );
            })()}
        </>
      )}
    </svg>
  );
}
